import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

interface OrganizationTransitionProps {
  isVisible: boolean;
  fromOrg: string | null;
  toOrg: string | null;
  onComplete: () => void;
}

type TransitionStage = 'farewell' | 'journey' | 'welcome';

const getTransitionMessages = (stage: TransitionStage, fromOrg?: string | null, toOrg?: string | null) => {
  const messages = {
    farewell: [
      "Thank you for the journey...",
      "Wrapping up with gratitude...",
      "Saying goodbye with style...",
      "Until we meet again...",
    ],
    journey: [
      "Traversing the digital cosmos...",
      "Sailing through organizational space...",
      "Dancing between dimensions...",
      "Surfing the ethereal waves...",
      "Floating through the cloud matrix...",
    ],
    welcome: [
      "Ready for new adventures!",
      "Your next chapter begins...",
      "Welcome to your new workspace!",
      "Let's make magic happen!",
    ]
  };
  
  const stageMessages = messages[stage];
  return stageMessages[Math.floor(Math.random() * stageMessages.length)];
};

export function OrganizationTransition({ 
  isVisible, 
  fromOrg, 
  toOrg, 
  onComplete 
}: OrganizationTransitionProps) {
  const [stage, setStage] = useState<TransitionStage>('farewell');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!isVisible) return;

    // Stage 1: Farewell (2500ms)
    setStage('farewell');
    setMessage(getTransitionMessages('farewell', fromOrg));
    
    const timer1 = setTimeout(() => {
      // Stage 2: Journey (2000ms)
      setStage('journey');
      setMessage(getTransitionMessages('journey'));
    }, 2500);

    const timer2 = setTimeout(() => {
      // Stage 3: Welcome (2500ms)
      setStage('welcome');
      setMessage(getTransitionMessages('welcome', null, toOrg));
    }, 4500);

    const timer3 = setTimeout(() => {
      onComplete();
    }, 7000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [isVisible, fromOrg, toOrg, onComplete]);

  const getStageConfig = (currentStage: TransitionStage) => {
    switch (currentStage) {
      case 'farewell':
        return {
          primary: '#ff6b6b',
          secondary: '#4ecdc4',
          accent: '#45b7d1',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          particles: { count: 25, colors: ['#ff6b6b', '#feca57', '#ff9ff3'] }
        };
      case 'journey':
        return {
          primary: '#74b9ff',
          secondary: '#0984e3',
          accent: '#a29bfe',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          particles: { count: 40, colors: ['#74b9ff', '#a29bfe', '#fd79a8', '#fdcb6e'] }
        };
      case 'welcome':
        return {
          primary: '#55efc4',
          secondary: '#00b894',
          accent: '#81ecec',
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          particles: { count: 35, colors: ['#55efc4', '#81ecec', '#74b9ff', '#a29bfe'] }
        };
    }
  };

  if (!isVisible) return null;

  const config = getStageConfig(stage);

  const overlay = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-background"
        style={{ 
          background: config.background,
          backdropFilter: 'blur(20px)'
        }}
      >
        {/* Colorful animated background layers */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute inset-0 opacity-40"
            style={{
              background: `radial-gradient(circle at 30% 40%, ${config.primary}40 0%, transparent 50%), radial-gradient(circle at 70% 60%, ${config.secondary}30 0%, transparent 50%)`,
            }}
          />
          <div 
            className="absolute inset-0"
            style={{
              background: `linear-gradient(45deg, ${config.accent}20, transparent 70%)`,
              opacity: 0.6,
              filter: 'blur(60px)',
              transform: stage === 'journey' ? 'scale(1.5) rotate(10deg)' : stage === 'welcome' ? 'scale(1.2) rotate(-5deg)' : 'scale(1)',
              transition: 'all 2s cubic-bezier(0.25, 0.1, 0.25, 1)'
            }}
          />
          <div 
            className="absolute inset-0"
            style={{
              background: `conic-gradient(from 0deg at 50% 50%, ${config.primary}10, ${config.secondary}10, ${config.accent}10, transparent)`,
              opacity: 0.3,
              filter: 'blur(80px)',
              animation: stage === 'journey' ? 'spin 20s linear infinite' : undefined
            }}
          />
        </div>
        
        {/* Content Overlay */}
        <div className="relative z-10 text-center px-8 max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={stage}
              initial={{ 
                opacity: 0, 
                y: 50, 
                scale: 0.9,
                filter: 'blur(10px)' 
              }}
              animate={{ 
                opacity: 1, 
                y: 0, 
                scale: 1,
                filter: 'blur(0px)' 
              }}
              exit={{ 
                opacity: 0, 
                y: -50, 
                scale: 1.1,
                filter: 'blur(10px)' 
              }}
              transition={{ 
                duration: 1.0, 
                ease: [0.25, 0.1, 0.25, 1] 
              }}
              className="space-y-8"
            >
              {/* Stage Icon */}
              <motion.div
                animate={{ 
                  rotate: stage === 'journey' ? 360 : 0,
                  scale: [1, 1.2, 1]
                }}
                transition={{ 
                  rotate: { duration: 3, repeat: Infinity, ease: 'linear' },
                  scale: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' }
                }}
                className="text-6xl mb-8"
              >
                {stage === 'farewell' && '👋'}
                {stage === 'journey' && '✨'}
                {stage === 'welcome' && '🎉'}
              </motion.div>

              {/* Main Message */}
              <motion.h1 
                className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight"
                style={{
                  color: 'white',
                  textShadow: '0 0 30px rgba(255,255,255,0.5), 0 0 60px rgba(255,255,255,0.3)'
                }}
              >
                {stage === 'farewell' && 'Farewell'}
                {stage === 'journey' && 'Journeying'}
                {stage === 'welcome' && 'Welcome'}
              </motion.h1>

              {/* Organization Name Display */}
              {(stage === 'farewell' && fromOrg) && (
                <motion.div
                  className="text-3xl md:text-4xl font-semibold mb-6"
                  style={{ color: 'rgba(255,255,255,0.9)' }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                >
                  {fromOrg}
                </motion.div>
              )}

              {(stage === 'welcome' && toOrg) && (
                <motion.div
                  className="text-3xl md:text-4xl font-semibold mb-6"
                  style={{ color: 'rgba(255,255,255,0.9)' }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                >
                  {toOrg}
                </motion.div>
              )}

              {/* Subtitle */}
              <motion.p 
                className="text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed"
                style={{
                  color: 'rgba(255,255,255,0.8)'
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                {message}
              </motion.p>

              {/* Loading Indicator */}
              <motion.div 
                className="flex justify-center space-x-2 mt-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 1.0 }}
              >
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: 'white' }}
                    animate={{
                      scale: [1, 1.5, 1],
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.3,
                      ease: 'easeInOut'
                    }}
                  />
                ))}
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Enhanced Star Field / Particle Effects */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Main Stars */}
          {Array.from({ length: config.particles.count }).map((_, i) => {
            const randomColor = config.particles.colors[Math.floor(Math.random() * config.particles.colors.length)];
            const size = Math.random() * 3 + 1; // 1-4px
            const x = Math.random() * 100;
            const y = Math.random() * 100;
            
            return (
              <motion.div
                key={`star-${i}`}
                className="absolute rounded-full"
                style={{ 
                  backgroundColor: randomColor,
                  left: `${x}%`,
                  top: `${y}%`,
                  width: `${size}px`,
                  height: `${size}px`,
                  boxShadow: `0 0 ${size * 3}px ${randomColor}, 0 0 ${size * 6}px ${randomColor}30`
                }}
                animate={{
                  scale: [0.8, 1.2, 0.8],
                  opacity: [0.4, 1, 0.4],
                  y: [0, -30, -60],
                  x: [0, Math.random() * 20 - 10, Math.random() * 40 - 20]
                }}
                transition={{
                  duration: 4 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 3,
                  ease: 'easeInOut'
                }}
              />
            );
          })}
          
          {/* Floating Orbs */}
          {Array.from({ length: Math.floor(config.particles.count / 3) }).map((_, i) => {
            const randomColor = config.particles.colors[Math.floor(Math.random() * config.particles.colors.length)];
            const size = Math.random() * 8 + 4; // 4-12px
            
            return (
              <motion.div
                key={`orb-${i}`}
                className="absolute rounded-full"
                style={{ 
                  background: `radial-gradient(circle, ${randomColor}80, ${randomColor}20)`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  width: `${size}px`,
                  height: `${size}px`,
                  filter: 'blur(1px)',
                  boxShadow: `0 0 ${size * 2}px ${randomColor}60`
                }}
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.3, 0.8, 0.3],
                  x: [0, Math.random() * 100 - 50],
                  y: [0, Math.random() * 100 - 50],
                  rotate: [0, 360]
                }}
                transition={{
                  duration: 8 + Math.random() * 4,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                  ease: 'linear'
                }}
              />
            );
          })}
          
          {/* Shooting Stars */}
          {stage === 'journey' && Array.from({ length: 5 }).map((_, i) => (
            <motion.div
              key={`shooting-${i}`}
              className="absolute"
              style={{ 
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 50}%`,
                width: '2px',
                height: '20px',
                background: `linear-gradient(to bottom, ${config.particles.colors[0]}, transparent)`,
                borderRadius: '1px'
              }}
              animate={{
                x: [0, 200],
                y: [0, 100],
                opacity: [0, 1, 0],
                scaleY: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: i * 0.8 + Math.random() * 2,
                ease: 'easeOut'
              }}
            />
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );

  if (typeof document !== 'undefined') {
    return createPortal(overlay, document.body);
  }
  return overlay;
}